import React, { useState, useContext, useEffect, useRef } from "react";
import {
  useMediaQuery,
  Toolbar,
  Box,
  Button,
  IconButton,
  Slide,
  Grow,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AuthContext } from "contexts/AuthProvider";
import PrimeSolutionLogo from "assets/images/logo/PrimeSolutionWhiteLogo.png";
import { MENUS } from "pages/Layout/config";
import { POWER_QUALITY_MENU } from "const";
import { useNavigate, useLocation } from "react-router-dom";
import MobileNavbar from "./MobileNavbar";
import Navbar from "./Navbar";
import { StyledAppBar } from "./style";
import { useQuery } from "@apollo/client";
import { GET_MY_CART } from "graphql/quote/queries";
// [보류] 견적 문의 카트 비활성화 (발주 카트만 사용)
// import { GET_MY_INQUIRY_CART } from "graphql/inquiry/queries";
import { safeJsonParse } from "utils";

// 드롭다운 메뉴 라벨: 국문 아래 영문을 작게 병기한다.
const MenuLabel = ({ ko, en }) => (
  <Box display="flex" flexDirection="column" alignItems="flex-start" lineHeight={1.2}>
    <Typography component="span" fontSize="inherit" fontWeight="inherit">
      {ko}
    </Typography>
    {en && (
      <Typography component="span" fontSize="0.72em" sx={{ opacity: 0.6 }}>
        {en}
      </Typography>
    )}
  </Box>
);

const Navigation = () => {
  const navigate = useNavigate();
  const { currentUser, signOut, isAdmin } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width:1220px)");
  const location = useLocation();
  const [isMainPage, setIsMainPage] = useState(location.pathname === "/");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoveredSubMenu, setHoveredSubMenu] = useState(null);

  // NEW: expand all grandchildren when any ">" submenu is hovered
  const [expandAllGrandchildren, setExpandAllGrandchildren] = useState(false);

  const hoverTimer = useRef(null);

  const { data } = useQuery(GET_MY_CART, {
    variables: { id: currentUser?.uid },
    skip: !currentUser || isAdmin,
  });
  // [보류] 견적 문의 카트 기능. 장바구니는 발주(quote)만 사용하도록 정리되어 비활성화.
  //        재사용 가능성이 있어 삭제하지 않고 주석으로 남겨둔다.
  // const { data: inquiryData } = useQuery(GET_MY_INQUIRY_CART, {
  //   variables: { userId: currentUser?.uid },
  //   skip: !currentUser || isAdmin,
  // });

  const orderList = data?.getMyCart?.orderList;
  const totalAmount = currentUser
    ? orderList?.length
    : safeJsonParse(sessionStorage.getItem("orderList"), []).length;

  // const inquiryList = inquiryData?.getMyInquiryCart;
  // const totalInquiryAmount = currentUser
  //   ? inquiryList?.length
  //   : safeJsonParse(sessionStorage.getItem("inquiryList"), []).length;

  // 대표 페이지가 만들어진 카테고리는 클릭 차단을 해제한다.
  // 노이즈 저감 솔루션은 이관할 기술자료가 없어 아직 페이지가 없다.
  const DISABLED_KEYS = [POWER_QUALITY_MENU.NOISE_REDUCTION_SOLUTION.key];

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleMouseEnterMenu = (menu) => {
    if (hoveredMenu !== menu) {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => {
        setHoveredMenu(menu);
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoveredMenu(null);
    setHoveredSubMenu(null);
    setExpandAllGrandchildren(false); // collapse all when leaving the toolbar area
  };

  const handleMouseLeaveMenu = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };

  // MODIFIED: when entering a submenu that has children, expand all grandchildren for that column
  const handleMouseEnterSubMenu = (subMenu) => {
    setHoveredSubMenu(subMenu);
    if (hasChildren(subMenu)) {
      setExpandAllGrandchildren(true);
    }
  };

  const navigateAndCloseMenu = (path) => {
    navigate(path);
    setDrawerOpen(false);
    setHoveredMenu(null);
    setHoveredSubMenu(null);
    setExpandAllGrandchildren(false);
  };

  useEffect(() => {
    setIsMainPage(location.pathname === "/");
    if (drawerOpen) setDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Utility: check if node has children
  const hasChildren = (node) =>
    Array.isArray(node?.children) && node.children.length > 0;

  return (
    <StyledAppBar
      position="static"
      isHovered={!!hoveredMenu}
      isMainPage={isMainPage}
    >
      <Toolbar className="toolbar" onMouseLeave={handleMouseLeave}>
        {isMobile ? (
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            sx={{ order: 2 }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        <Button
          onClick={() => navigateAndCloseMenu("/")}
          className="logo-button"
        >
          <img src={PrimeSolutionLogo} alt="Prime Solution  Logo" />
        </Button>

        {isMobile ? (
          <MobileNavbar
            drawerOpen={drawerOpen}
            handleDrawerToggle={() => setDrawerOpen(false)}
            menus={MENUS}
            currentUser={currentUser}
            pathname={location.pathname}
            quoteCount={totalAmount}
            isAdmin={isAdmin}
          />
        ) : (
          <Navbar
            menus={MENUS}
            currentUser={currentUser}
            hoveredMenu={hoveredMenu}
            handleMouseEnter={handleMouseEnterMenu}
            handleMouseLeave={handleMouseLeaveMenu}
            signOut={signOut}
            handleSignInClick={() => navigateAndCloseMenu("/signin")}
            quoteCount={totalAmount}
            isAdmin={isAdmin}
          />
        )}

        {/* submenu container: only when there is hoveredMenu with children */}
        {!isMobile && hoveredMenu && hasChildren(hoveredMenu) && (
          <Slide
            in={!!hoveredMenu}
            direction="right"
            mountOnEnter
            unmountOnExit
            timeout={300}
          >
            <Box className="submenu-columns">
              <Box className="columns-container">
                {hoveredMenu.children?.map((item, index) => (
                  <Box
                    key={item.key}
                    className="submenu-column"
                    onMouseEnter={() => setHoveredSubMenu(hoveredSubMenu)} // keep state; no-op
                  >
                    {/* 1-depth: NO indicator/bold even if it has children */}
                    <Slide in direction="right" timeout={150 * (index + 1)}>
                      <Box width="250px">
                        <Button
                          onClick={() => {
                            if (item.to) navigateAndCloseMenu(item.to);
                          }}
                          className="title"
                          disabled={!item.to}
                          sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                            width: "100%",
                          }}
                        >
                          <MenuLabel ko={item.name} en={item.englishName} />
                        </Button>
                      </Box>
                    </Slide>

                    {/* 2-depth */}
                    <Box className="children">
                      {item.children?.map((subItem) => {
                        const isDisabled = DISABLED_KEYS.includes(subItem.key);
                        const subHasChildren = hasChildren(subItem);

                        return (
                          <Slide
                            in
                            direction="right"
                            timeout={200 * (index + 1)}
                            key={subItem.key}
                          >
                            <Button
                              disableRipple={isDisabled}
                              onClick={() => {
                                if (isDisabled) return;
                                if (subItem.to)
                                  navigateAndCloseMenu(subItem.to);
                              }}
                              onMouseEnter={() =>
                                handleMouseEnterSubMenu(subItem)
                              }
                              endIcon={
                                subHasChildren ? (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                ) : null
                              }
                              aria-haspopup={
                                subHasChildren ? "menu" : undefined
                              }
                              aria-expanded={
                                subHasChildren
                                  ? expandAllGrandchildren ||
                                    (hoveredSubMenu === subItem
                                      ? "true"
                                      : undefined)
                                  : undefined
                              }
                              sx={{
                                cursor: isDisabled ? "default" : "pointer",
                                fontWeight: subHasChildren ? 600 : 400, // indicator starts from 2-depth
                                justifyContent: "space-between",
                                textTransform: "none",
                                width: "100%",
                                pr: subHasChildren ? 1 : undefined,
                              }}
                            >
                              <MenuLabel ko={subItem.name} en={subItem.englishName} />
                            </Button>
                          </Slide>
                        );
                      })}
                    </Box>

                    {/* 3-depth */}
                    <Box>
                      {item.children?.map((subItem) => {
                        // Decide whether to render grandchildren for this subItem
                        const renderForThisSub =
                          (expandAllGrandchildren && hasChildren(subItem)) ||
                          (hoveredSubMenu === subItem && hasChildren(subItem));

                        if (!renderForThisSub) return null;

                        return subItem.children?.map((child, childIndex) => {
                          const childHasChildren = hasChildren(child);
                          return (
                            <Grow
                              in
                              timeout={200 * (childIndex + 1)}
                              key={child.key}
                            >
                              <Box className="grandchildren">
                                <Button
                                  onClick={() => navigateAndCloseMenu(child.to)}
                                  endIcon={
                                    childHasChildren ? (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    ) : null
                                  }
                                  aria-haspopup={
                                    childHasChildren ? "menu" : undefined
                                  }
                                  sx={{
                                    fontWeight: childHasChildren ? 600 : 400,
                                    justifyContent: "space-between",
                                    textTransform: "none",
                                    width: "100%",
                                    pr: childHasChildren ? 1 : undefined,
                                  }}
                                >
                                  <MenuLabel ko={child.name} en={child.englishName} />
                                </Button>
                              </Box>
                            </Grow>
                          );
                        });
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Slide>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default Navigation;
